import { useEffect, useRef, useCallback } from "react";

interface UseAutoScrollOptions {
  containerRef: React.RefObject<HTMLElement>;
  messages: unknown[];
  loading?: boolean;
  threshold?: number;
  throttleMs?: number;
}

const PROGRAMMATIC_SCROLL_RESET_DELAY_MS = 50;

export function useAutoScroll({
  containerRef,
  messages,
  loading = false,
  threshold = 200,
  throttleMs = 100,
}: UseAutoScrollOptions) {
  const scrollAnimationFrameRef = useRef<number | null>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastMessagesLengthRef = useRef(messages.length);
  const lastScrollTimeRef = useRef(0);
  const autoScrollDisabledRef = useRef(false);
  const lastScrollTopRef = useRef(0);
  const programmaticScrollTimeRef = useRef(0);

  const isAtBottom = useCallback((): boolean => {
    const container = containerRef.current;
    if (!container) {
      return false;
    }
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    return distanceFromBottom < threshold;
  }, [containerRef, threshold]);

  const cancelPendingScrolls = useCallback(() => {
    if (scrollAnimationFrameRef.current !== null) {
      cancelAnimationFrame(scrollAnimationFrameRef.current);
      scrollAnimationFrameRef.current = null;
    }
    if (scrollTimeoutRef.current !== null) {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }
  }, []);

  const scrollToBottomIfNeeded = useCallback(() => {
    if (autoScrollDisabledRef.current) {
      return;
    }

    const container = containerRef.current;
    if (!container) {
      return;
    }

    if (!isAtBottom()) {
      return;
    }

    programmaticScrollTimeRef.current = Date.now();
    container.scrollTop = container.scrollHeight;

    setTimeout(() => {
      programmaticScrollTimeRef.current = 0;
    }, PROGRAMMATIC_SCROLL_RESET_DELAY_MS);
  }, [isAtBottom]);

  const scheduleScroll = useCallback(() => {
    cancelPendingScrolls();
    scrollAnimationFrameRef.current = requestAnimationFrame(() => {
      scrollAnimationFrameRef.current = null;
      scrollToBottomIfNeeded();
    });
  }, [cancelPendingScrolls, scrollToBottomIfNeeded]);

  const scheduleThrottledScroll = useCallback(() => {
    const now = Date.now();
    const timeSinceLastScroll = now - lastScrollTimeRef.current;

    if (timeSinceLastScroll < throttleMs) {
      if (scrollTimeoutRef.current !== null) {
        return;
      }

      const delay = throttleMs - timeSinceLastScroll;
      scrollTimeoutRef.current = setTimeout(() => {
        scrollTimeoutRef.current = null;
        lastScrollTimeRef.current = Date.now();
        scheduleScroll();
      }, delay);
      return;
    }

    lastScrollTimeRef.current = now;
    scheduleScroll();
  }, [throttleMs, scheduleScroll]);

  useEffect(() => {
    const messagesLengthChanged = messages.length !== lastMessagesLengthRef.current;
    lastMessagesLengthRef.current = messages.length;

    if (messagesLengthChanged) {
      scheduleScroll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  useEffect(() => {
    if (!loading) {
      return undefined;
    }

    scheduleThrottledScroll();

    return () => {
      if (scrollTimeoutRef.current !== null) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
    };
  }, [messages, loading, scheduleThrottledScroll]);

  useEffect(() => {
    if (!loading) {
      scheduleScroll();
    }
  }, [loading, scheduleScroll]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return undefined;
    }

    const handleScroll = () => {
      const now = Date.now();
      const timeSinceProgrammaticScroll = now - programmaticScrollTimeRef.current;

      if (timeSinceProgrammaticScroll < PROGRAMMATIC_SCROLL_RESET_DELAY_MS) {
        lastScrollTopRef.current = container.scrollTop;
        return;
      }

      const currentScrollTop = container.scrollTop;
      const previousScrollTop = lastScrollTopRef.current;
      lastScrollTopRef.current = currentScrollTop;

      const scrolledUp = currentScrollTop < previousScrollTop - 1;
      const atBottom = isAtBottom();

      if (scrolledUp) {
        autoScrollDisabledRef.current = true;
        cancelPendingScrolls();
      } else if (atBottom) {
        autoScrollDisabledRef.current = false;
      }
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    lastScrollTopRef.current = container.scrollTop;

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [containerRef, isAtBottom, cancelPendingScrolls]);

  useEffect(() => {
    return () => {
      cancelPendingScrolls();
    };
  }, [cancelPendingScrolls]);
}
